import { VisionaryScene } from '@/visionaryControllers/visionaryScene';
import * as THREE from 'three';
import { globalConfig, globalTools } from '@/utils/globalConfig';
import { animationUtils } from '@/utils/animationUtils';
import { getI18nText } from '@/utils/commonFunc';

const FRAME_PREFIX = 'frame_';

export class VisObjectFrameInfo {
    objectId: string;
    position: THREE.Vector3;
    rotation: THREE.Euler;
    scale: THREE.Vector3;
    // gaussianParams: any | null; // 高斯模型参数，参考上面的VisModelGaussianParams
    cameraParams: any | null; // 渲染相机参数，fov、near、far

    // constructor(objectId: string, position: THREE.Vector3, rotation: THREE.Euler, scale: THREE.Vector3, gaussianParams: any | null, cameraParams: any | null) {
    //     this.objectId = objectId;
    //     this.position = new THREE.Vector3(position.x, position.y, position.z);
    //     this.rotation = new THREE.Euler(rotation.x, rotation.y, rotation.z);
    //     this.scale = new THREE.Vector3(scale.x, scale.y, scale.z);

    //     this.gaussianParams = gaussianParams ? gaussianParams : null;
    //     this.cameraParams = cameraParams ? cameraParams : null;
    // }
    constructor(objectId: string, position: THREE.Vector3, rotation: THREE.Euler, scale: THREE.Vector3, cameraParams: any | null) {
        this.objectId = objectId;
        this.position = new THREE.Vector3(position.x, position.y, position.z);
        this.rotation = new THREE.Euler(rotation.x, rotation.y, rotation.z);
        this.scale = new THREE.Vector3(scale.x, scale.y, scale.z);

        this.cameraParams = cameraParams ? cameraParams : null;
    }
}

export class VisCameraFrameInfo {
    position: THREE.Vector3;
    rotation: THREE.Euler;
    scale: THREE.Vector3;
    fov: number;

    constructor(position: THREE.Vector3, rotation: THREE.Euler, scale: THREE.Vector3, fov: number) {
        this.position = new THREE.Vector3(position.x, position.y, position.z);
        this.rotation = new THREE.Euler(rotation.x, rotation.y, rotation.z);
        this.scale = new THREE.Vector3(scale.x, scale.y, scale.z);
        this.fov = fov;
    }
}

/**
 * 关键帧信息类
 * by helen - 改用渲染相机来记录关键帧，所以不再使用 VisCameraFrameInfo
 */
export class VisFrameInfo {
    index: number;
    camera: VisCameraFrameInfo | null;
    objects: VisObjectFrameInfo[];

    constructor(index: number, cameraPosition?: THREE.Vector3, cameraRotation?: THREE.Euler, cameraScale?: THREE.Vector3, fov?: number) {
        this.index = index;
        if (cameraPosition && cameraRotation && cameraScale && fov !== undefined) {
            this.camera = new VisCameraFrameInfo(cameraPosition, cameraRotation, cameraScale, fov);
        } else {
            this.camera = null;
        }
        this.objects = [];
    }
    // constructor(index: number) {
    //     this.index = index;
    //     this.objects = [];
    // }
}

// 动画系统类
export class AnimationController {
    private visionaryScene: VisionaryScene;
    private currentIndex: number = 0;

    private animationId: number | null = null;

    // 新增帧率和总帧数控制
    private frameRate: number = globalConfig.frameRate; // 帧率 (FPS)
    private totalFrames: number = globalConfig.totalFrames; // 总帧数

    // 帧率控制相关属性
    private targetFrameTime: number = 1000 / globalConfig.frameRate; // 目标帧间隔（毫秒）

    public globalKeyframes: VisFrameInfo[] = [];
    private handleUpdateProgress: ((progress: number, textcontent: string) => void) | null = null;
    private handleUpdateKeyframe: ((keyframes: VisFrameInfo[]) => void) | null = null;
    private handleModelAnimationChange: (event: Event) => void;

    // 录制回调机制：当帧索引更新时，触发所有注册的回调
    private recordingCallbacks: Array<() => void | Promise<void>> = [];

    // @kangan 用于播放的时候控制场景相机
    // 场景相机的信息仍然记录，但不一定播放
    private animateCameraDuringPlayback: boolean = false;

    constructor(scene: VisionaryScene) {
        // console.log('AnimationSystem constructor');
        this.visionaryScene = scene;
        this.currentIndex = 0;

        this.animationId = null;

        // 初始化帧率和总帧数
        this.frameRate = globalConfig.frameRate;
        this.totalFrames = globalConfig.totalFrames;

        // 全局关键帧（影响所有对象）
        this.setGlobalKeyframes();
        this.handleModelAnimationChange = (event: Event) => {
            const detail = (event as CustomEvent).detail;
            if (!globalConfig.sceneParams.isPlaying) {
                if (detail) {
                    this.visionaryScene.setGaussianAnimationTime(-1);
                } else {
                    let time = this.currentIndex / this.frameRate;
                    this.visionaryScene.setGaussianAnimationTime(time);
                }
            }
        }
        this.setupEventListeners();
    }

    setupEventListeners() {
        window.addEventListener('event.modelAnimationChange', this.handleModelAnimationChange);
    }

    setGlobalKeyframes() {
        this.globalKeyframes = animationUtils.getGlobalKeyframesByExistScene();
    }
    // 设置更新进度回调函数
    setHandleUpdateProgress(handleUpdateProgress: (progress: number, textcontent: string) => void) {
        this.handleUpdateProgress = handleUpdateProgress;
    }

    // 设置更新关键帧回调函数
    setHandleUpdateKeyframe(handleUpdateKeyframe: (keyframes: VisFrameInfo[]) => void) {
        this.handleUpdateKeyframe = handleUpdateKeyframe;
    }

    setAnimateCameraDuringPlayback(enable: boolean) {
        this.animateCameraDuringPlayback = enable;
    }

    // 新增帧率和总帧数控制方法
    setFrameRate(frameRate: number) {
        this.frameRate = frameRate;
        this.targetFrameTime = 1000 / this.frameRate;
    }

    setTotalFrames(totalFrames: number) {
        this.totalFrames = totalFrames;
        // 如果当前关键帧比totalFrames大，则删除当前关键帧
        if (this.currentIndex > totalFrames - 1) {
            this.currentIndex = totalFrames - 1;
        }
        this.globalKeyframes = this.globalKeyframes.filter(k => k.index < totalFrames);
    }

    getFrameRate() {
        return this.frameRate;
    }

    getTotalFrames() {
        return this.totalFrames;
    }

    getCurrentIndex() {
        return this.currentIndex;
    }

    getGlobalKeyframes() {
        return this.globalKeyframes;
    }

    public replaceGlobalKeyframes(frames: VisFrameInfo[]): void {
        if (Array.isArray(frames)) {
            this.globalKeyframes = [...frames].sort((a, b) => a.index - b.index);
        } else {
            this.globalKeyframes = [];
        }
        // debugger;
        this.currentIndex = this.globalKeyframes.length ? this.globalKeyframes[0].index : 0;
        if (this.handleUpdateKeyframe) {
            this.handleUpdateKeyframe(this.globalKeyframes);
        }
    }

    // 
    public loadKeyframesFromSerialized(entries: Array<{ objectId: string; frame: number; trs: [number[], number[], number[]]; cameraParams?: any }>): void { // 取消高斯参数对动画的影响
        if (!Array.isArray(entries)) {
            this.replaceGlobalKeyframes([]);
            return;
        }

        const frameMap = new Map<number, VisFrameInfo>();

        const toVector3 = (source: any[], defaultValues: [number, number, number]) => {
            const ensure = (value: any, fallback: number) => {
                const num = Number(value);
                return Number.isFinite(num) ? num : fallback;
            };
            const x = ensure(source?.[0], defaultValues[0]);
            const y = ensure(source?.[1], defaultValues[1]);
            const z = ensure(source?.[2], defaultValues[2]);
            return new THREE.Vector3(x, y, z);
        };
        // debugger;
        for (const entry of entries) {
            if (!entry || typeof entry !== 'object') continue;
            const objectId = entry.objectId;
            const frameIndex = Number(entry.frame);
            if (!objectId || !Number.isFinite(frameIndex)) continue;

            const base = Array.isArray(entry.trs) ? entry.trs : [];
            const positionArr = Array.isArray(base[0]) ? base[0] as number[] : [0, 0, 0];
            const rotationArr = Array.isArray(base[1]) ? base[1] as number[] : [0, 0, 0];
            const scaleArr = Array.isArray(base[2]) ? base[2] as number[] : [1, 1, 1];

            let frameInfo = frameMap.get(frameIndex);
            if (!frameInfo) {
                frameInfo = new VisFrameInfo(frameIndex);
                frameMap.set(frameIndex, frameInfo);
            }

            const position = toVector3(positionArr, [0, 0, 0]);
            const rotation = new THREE.Euler(rotationArr[0] ?? 0, rotationArr[1] ?? 0, rotationArr[2] ?? 0);
            const scale = toVector3(scaleArr, [1, 1, 1]);

            // const objectInfo = new VisObjectFrameInfo(objectId, position, rotation, scale, entry.gaussianParams ?? null, entry.cameraParams ?? null);
            const objectInfo = new VisObjectFrameInfo(objectId, position, rotation, scale, entry.cameraParams ?? null);
            frameInfo.objects.push(objectInfo);
        }

        const frames = Array.from(frameMap.values()).sort((a, b) => a.index - b.index);
        this.replaceGlobalKeyframes(frames);
    }

    /**
     * 获取最后一个关键帧的索引
     * 如果没有关键帧，返回 -1
     * @returns 最后一个关键帧的索引，如果没有关键帧则返回 -1
     */
    getLastKeyframeIndex(): number {
        if (this.globalKeyframes.length === 0) {
            return -1;
        }
        // globalKeyframes 已经按 index 排序，最后一个就是最大的索引
        return this.globalKeyframes[this.globalKeyframes.length - 1].index;
    }

    /**
     * 设置当前帧索引（不播放动画，仅设置状态）
     * 用于导出视频时基于帧索引驱动渲染
     * @param frameIndex 帧索引（0 到 totalFrames）
     * @returns Promise，等待所有回调完成
     */
    async setFrameIndex(frameIndex: number): Promise<void> {
        // // 限制帧索引范围
        // this.currentIndex = Math.max(0, Math.min(frameIndex, this.totalFrames - 1));

        // // 根据帧索引插值关键帧并更新场景状态
        // // 这会自动计算动画时间并更新所有动态模型（ONNX）
        // this.interpolateObjects();

        // // 更新进度显示
        // this.updateProgress();
        globalTools.timelinePanelTools.setExpanded(true);
        globalTools.timelinePanelTools.timelineControl.setCurrentTick(frameIndex);

        // ✅ 触发录制回调（如果正在录制），并等待所有回调完成
        const callbackPromises: Promise<void>[] = [];
        this.recordingCallbacks.forEach(cb => {
            try {
                const result = cb();
                if (result instanceof Promise) {
                    callbackPromises.push(
                        result.catch((error) => {
                            console.error('录制回调执行错误:', error);
                            // 不抛出错误，确保其他回调能继续执行
                        }).then(() => void 0)
                    );
                }
            } catch (error) {
                console.error('录制回调执行错误:', error);
            }
        });

        // 等待所有异步回调完成
        if (callbackPromises.length > 0) {
            await Promise.all(callbackPromises);
        }
    }

    /**
     * 注册录制回调
     * 当帧索引更新时，会触发此回调
     * @param callback 回调函数
     * @returns 取消注册函数
     * @deprecated 使用 registerFrameUpdateCallback 代替
     */
    registerRecordingCallback(callback: () => void | Promise<void>): () => void {
        return this.registerFrameUpdateCallback(callback);
    }

    /**
     * 注册帧更新回调（Core 接口兼容）
     * 当帧索引更新时，会触发此回调
     * @param callback 回调函数
     * @returns 取消注册函数
     */
    registerFrameUpdateCallback(callback: () => void | Promise<void>): () => void {
        this.recordingCallbacks.push(callback);
        // 返回取消注册函数
        return () => {
            const index = this.recordingCallbacks.indexOf(callback);
            if (index > -1) {
                this.recordingCallbacks.splice(index, 1);
            }
        };
    }
    updateCameraParams(position: { x: number, y: number, z: number }, rotation: { x: number, y: number, z: number }, scale: { x: number, y: number, z: number }) {
        this.visionaryScene.updateCameraParams(position, rotation, scale);
    }

    addKeyframe(frameIndex: number) {
        if (isNaN(frameIndex) || frameIndex < 0 || frameIndex > this.totalFrames) {
            alert(getI18nText('errorinfo.invalidFrameNumber', { frameIndex, totalFrames: this.totalFrames }));
            return;
        }

        let camera = this.visionaryScene.getCamera();
        if (!camera) {
            alert(getI18nText('errorinfo.cannotGetCameraInfo'));
            return;
        }
        // // 创建全局关键帧，包含所有对象和相机的状态
        const globalKeyframe = this.visionaryScene.getSceneFrameInfo(frameIndex);
        if (globalKeyframe) {
            // globalKeyframe.index = frameIndex;
            // globalKeyframe.camera = null;
            if (this.globalKeyframes.find(k => k.index === frameIndex)) { // 如果存在，则删除原来的关键帧
                this.globalKeyframes.splice(this.globalKeyframes.findIndex(k => k.index === frameIndex), 1);
            }
            console.log('添加全局关键帧: 所有对象 在第', frameIndex, '帧', this.globalKeyframes);
            this.globalKeyframes.push(globalKeyframe);
            this.globalKeyframes.sort((a, b) => a.index - b.index);
            if (this.handleUpdateKeyframe) { // 如果回调函数存在，则调用回调函数,主要用来同步UI更新
                this.handleUpdateKeyframe(this.globalKeyframes);
            }
            // console.log(`添加全局关键帧: 所有对象 在第 ${frameIndex} 帧`, 'info', this.globalKeyframes);
        }
    }

    // 跳转到指定帧的动画帧
    goToAnimationFrameByIndex(index: number) {
        if (index < 0 || index > this.totalFrames) {
            alert(getI18nText('errorinfo.invalidFrameNumber', { frameIndex: index, totalFrames: this.totalFrames }));
            return;
        }
        this.currentIndex = index;
        this.updateProgress();
        // console.log(`跳转到动画帧: ${index}`, 'info');
        this.interpolateObjects();
    }

    // 删除全局关键帧
    deleteGlobalKeyframe(index: number) {
        const keyframeIndex = this.globalKeyframes.findIndex(k => k.index === index);
        if (keyframeIndex >= 0) { // 如果存在，则删除原来的关键帧
            this.globalKeyframes.splice(keyframeIndex, 1);
            if (this.handleUpdateKeyframe) { // 如果回调函数存在，则调用回调函数,主要用来同步UI更新
                this.handleUpdateKeyframe(this.globalKeyframes);
            }
            // console.log(`删除全局关键帧: ${index}`, 'info');
        }
    }

    updateProgress() {
        const progress = (this.currentIndex / this.totalFrames) * 100;
        // 计算当前帧数
        const currentFrame = Math.floor(this.currentIndex);
        const textcontent = `${currentFrame}/${this.totalFrames}`;
        if (this.handleUpdateProgress) {
            this.handleUpdateProgress(progress, textcontent);
        }
    }

    getObjectKeyframeRage(id: string): { beforeKeyframe: VisFrameInfo | null, afterKeyframe: VisFrameInfo | null } {
        // 筛选出包含指定 objectId 的关键帧
        const keyframesWithObject = this.globalKeyframes.filter(k =>
            k.objects.some(o => o.objectId === id)
        );

        if (keyframesWithObject.length === 0) {
            // 如果没有找到任何包含该 objectId 的关键帧，返回 null
            return { beforeKeyframe: null, afterKeyframe: null };
        }

        if (keyframesWithObject.length === 1) {
            // 如果只有一帧满足条件，前后帧都是这一帧
            return {
                beforeKeyframe: keyframesWithObject[0],
                afterKeyframe: keyframesWithObject[0]
            };
        }

        // 向前查找：找到索引 <= currentIndex 且包含该 objectId 的帧
        let beforeKeyframe: VisFrameInfo | null = null;
        // 从后往前查找，找到最接近 currentIndex 且 <= currentIndex 的帧
        for (let i = keyframesWithObject.length - 1; i >= 0; i--) {
            if (keyframesWithObject[i].index <= this.currentIndex) {
                beforeKeyframe = keyframesWithObject[i];
                break;
            }
        }

        // 向后查找：找到索引 >= currentIndex 且包含该 objectId 的帧
        let afterKeyframe: VisFrameInfo | null = null;
        // 从前往后查找，找到最接近 currentIndex 且 >= currentIndex 的帧
        for (let i = 0; i < keyframesWithObject.length; i++) {
            if (keyframesWithObject[i].index >= this.currentIndex) {
                afterKeyframe = keyframesWithObject[i];
                break;
            }
        }

        // 支持循环查找
        if (!beforeKeyframe) {
            // 如果向前找不到，从最后一个关键帧循环（最后一个关键帧作为 beforeKeyframe）
            beforeKeyframe = keyframesWithObject[keyframesWithObject.length - 1];
        }
        if (!afterKeyframe) {
            // 如果向后找不到，从第一个关键帧循环（第一个关键帧作为 afterKeyframe）
            afterKeyframe = keyframesWithObject[0];
        }

        return { beforeKeyframe, afterKeyframe };
    }

    // 插值所有对象和相机，计算线性插值 - helen - 待优化，需要确认插值是否正确，尤其是高斯模型参数的插值
    interpolateObjects() {
        if (globalConfig.sceneParams.isPlaying) {
            let time = this.currentIndex / this.frameRate;
            this.visionaryScene.setGaussianAnimationTime(time);
        } else {
            if (globalConfig.sceneParams.isAnimation) {
                this.visionaryScene.setGaussianAnimationTime(-1);
            } else {
                let time = this.currentIndex / this.frameRate;
                this.visionaryScene.setGaussianAnimationTime(time);
            }
        }
        if (this.globalKeyframes.length < 2) return // 如果关键帧数量小于2，则其他静止，4D高斯设置动画时间随当前帧变化
        // console.log('当前帧-------', this.currentIndex, this.totalFrames, this.frameRate);
        // 找到当前帧前后的全局关键帧
        let beforeKeyframe: VisFrameInfo | null = null;
        let afterKeyframe: VisFrameInfo | null = null;

        // 获取最小和最大关键帧索引
        const minKeyframeIndex = this.globalKeyframes[0].index;
        const maxKeyframeIndex = this.globalKeyframes[this.globalKeyframes.length - 1].index;

        // 处理边界情况：循环查找
        if (this.currentIndex < minKeyframeIndex) {
            // 当前帧小于最小关键帧：从最后一个关键帧循环到第一个关键帧
            beforeKeyframe = this.globalKeyframes[minKeyframeIndex];
            afterKeyframe = this.globalKeyframes[minKeyframeIndex];
        } else if (this.currentIndex > maxKeyframeIndex) {
            // 当前帧大于最大关键帧：从最后一个关键帧循环到第一个关键帧
            beforeKeyframe = this.globalKeyframes[maxKeyframeIndex];
            afterKeyframe = this.globalKeyframes[maxKeyframeIndex];
        } else {
            // 正常情况：查找包含当前帧的关键帧区间
            for (let i = 0; i < this.globalKeyframes.length - 1; i++) {
                if (this.currentIndex >= this.globalKeyframes[i].index &&
                    this.currentIndex <= this.globalKeyframes[i + 1].index) {
                    beforeKeyframe = this.globalKeyframes[i];
                    afterKeyframe = this.globalKeyframes[i + 1];
                    break;
                }
            }
        }

        if (!beforeKeyframe || !afterKeyframe) return;

        // 计算插值比例 t (0-1之间)
        // 基于帧数计算插值比例，确保平滑过渡
        let frameRange: number;
        let currentFrameInRange: number;

        // 正常情况：直接计算帧范围
        frameRange = afterKeyframe.index - beforeKeyframe.index;
        currentFrameInRange = this.currentIndex - beforeKeyframe.index;

        const t = frameRange > 0 ? currentFrameInRange / frameRange : 0;

        // 确保 t 在 0-1 范围内
        const clampedT = Math.max(0, Math.min(1, t));

        // 插值相机状态
        if (this.animateCameraDuringPlayback && beforeKeyframe.camera && afterKeyframe.camera) {
            const px = this.lerp(beforeKeyframe.camera.position.x, afterKeyframe.camera.position.x, clampedT);
            const py = this.lerp(beforeKeyframe.camera.position.y, afterKeyframe.camera.position.y, clampedT);
            const pz = this.lerp(beforeKeyframe.camera.position.z, afterKeyframe.camera.position.z, clampedT);
            const prx = this.lerp(beforeKeyframe.camera.rotation.x, afterKeyframe.camera.rotation.x, clampedT);
            const pry = this.lerp(beforeKeyframe.camera.rotation.y, afterKeyframe.camera.rotation.y, clampedT);
            const prz = this.lerp(beforeKeyframe.camera.rotation.z, afterKeyframe.camera.rotation.z, clampedT);
            const pscale = this.lerp(beforeKeyframe.camera.scale.x, afterKeyframe.camera.scale.x, clampedT);
            const pscaley = this.lerp(beforeKeyframe.camera.scale.y, afterKeyframe.camera.scale.y, clampedT);
            const pscalez = this.lerp(beforeKeyframe.camera.scale.z, afterKeyframe.camera.scale.z, clampedT);
            const pfov = this.lerp(beforeKeyframe.camera.fov, afterKeyframe.camera.fov, clampedT);

            this.updateCameraParams({ x: px, y: py, z: pz }, { x: prx, y: pry, z: prz }, { x: pscale, y: pscaley, z: pscalez });
            this.visionaryScene.updateCameraFOV(pfov as number);
        }

        // 插值所有对象
        this.visionaryScene.getObjects().forEach((obj: any) => {
            let beforeObj = beforeKeyframe.objects.find(o => o.objectId === obj.id);
            let afterObj = afterKeyframe.objects.find(o => o.objectId === obj.id);

            if (!beforeObj || !afterObj) {
                const searchResult = this.getObjectKeyframeRage(obj.id);
                if (searchResult.beforeKeyframe && searchResult.afterKeyframe) {
                    beforeObj = searchResult.beforeKeyframe.objects.find(o => o.objectId === obj.id);
                    afterObj = searchResult.afterKeyframe.objects.find(o => o.objectId === obj.id);
                }
                if (!beforeObj || !afterObj) {
                    return;
                }
            };
            // console.log('插值对象-------', beforeObj, afterObj);
            const newParams: {
                position: { x: number, y: number, z: number },
                rotation: { x: number, y: number, z: number },
                scale: { x: number, y: number, z: number },
                // gaussianParams: any | null,
                cameraParams: any | null
            } = {
                position: {
                    x: this.lerp(beforeObj.position.x, afterObj.position.x, clampedT),
                    y: this.lerp(beforeObj.position.y, afterObj.position.y, clampedT),
                    z: this.lerp(beforeObj.position.z, afterObj.position.z, clampedT),
                },
                rotation: {
                    x: this.lerp(beforeObj.rotation.x, afterObj.rotation.x, clampedT),
                    y: this.lerp(beforeObj.rotation.y, afterObj.rotation.y, clampedT),
                    z: this.lerp(beforeObj.rotation.z, afterObj.rotation.z, clampedT),
                },
                scale: {
                    x: this.lerp(beforeObj.scale.x, afterObj.scale.x, clampedT),
                    y: this.lerp(beforeObj.scale.y, afterObj.scale.y, clampedT),
                    z: this.lerp(beforeObj.scale.z, afterObj.scale.z, clampedT),
                },
                // gaussianParams: null,
                cameraParams: null,
            };

            if (beforeObj.cameraParams && afterObj.cameraParams) {
                newParams.cameraParams = {
                    fov: this.lerp(beforeObj.cameraParams.fov, afterObj.cameraParams.fov, clampedT),
                    near: this.lerp(beforeObj.cameraParams.near, afterObj.cameraParams.near, clampedT),
                    far: this.lerp(beforeObj.cameraParams.far, afterObj.cameraParams.far, clampedT),
                    aspect: this.lerp(beforeObj.cameraParams.aspect, afterObj.cameraParams.aspect, clampedT),
                };
                // console.log('插值渲染相机参数-------', newParams.cameraParams);
            }
            this.visionaryScene.updateObjectParams(obj.id, newParams);
        });
    }


    lerp(a: number, b: number, t: number): number {
        return a + (b - a) * t;
    }

    public destroy(): void {
        // 停止动画播放并取消 requestAnimationFrame
        this.visionaryScene.stopAnimation();

        // 清理帧插值状态
        this.currentIndex = 0;

        // 释放回调和关键帧引用，避免内存泄漏
        this.globalKeyframes.length = 0;
        this.recordingCallbacks.length = 0;
        this.handleUpdateProgress = null;
        this.handleUpdateKeyframe = null;
        window.removeEventListener('event.modelAnimationChange', this.handleModelAnimationChange);
    }
}

